/* eslint-disable max-lines */
import { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import { colors, layout } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import { pdfByType } from '../../../../../helpers/pdfByType';
import { buildLaptopContractPdf } from '../utils/pdfBuilders';
import { useLivePdfPreview } from '../utils/livePdfPreview';
import { getPreEmploymentLookup } from '../utils/preEmploymentLookup';
import { formatPhone } from '../utils/numberValidation';
import { BaseStepProps } from './BaseStepProps';

interface LaptopContractDraft {
  employeeName: string;
  initials: string;
  noticeAddress: string;
  noticeCityStateZip: string;
  noticeEmail: string;
  noticePhone: string;
  laptopMake: string;
  laptopModel: string;
  laptopSerial: string;
}

const empty: LaptopContractDraft = {
  employeeName: '',
  initials: '',
  noticeAddress: '',
  noticeCityStateZip: '',
  noticeEmail: '',
  noticePhone: '',
  laptopMake: '',
  laptopModel: '',
  laptopSerial: '',
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const buildInitialsFromName = (name: string): string => {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 4);
};

const buildCityStateZip = (city: string, state: string, zip: string): string =>
  [city, state, zip].filter(Boolean).join(', ');

const LaptopContractStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  application,
  applicationDraft,
  draftDocs,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const config = pdfByType.find((p) => p.type === 'LAPTOP_CONTRACT');
  const pdfUrl = step.pdfUrl || config?.url;
  const backendDraft = draftDocs.find((d) => d.type === 'LAPTOP_CONTRACT');
  const lookup = getPreEmploymentLookup(application, applicationDraft, token);

  const initialFromBackend: LaptopContractDraft = {
    ...empty,
    employeeName: lookup.fullName,
    initials: buildInitialsFromName(lookup.fullName),
    noticeAddress: lookup.address,
    noticeCityStateZip: buildCityStateZip(lookup.city, lookup.state, lookup.zipCode),
    noticeEmail: lookup.email,
    noticePhone: lookup.phone,
    ...(backendDraft?.options?.fields || {}),
  };
  const initial = (draft as LaptopContractDraft) || initialFromBackend;
  const [data, setData] = useState<LaptopContractDraft>({ ...empty, ...initial });
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const update = (patch: Partial<LaptopContractDraft>) => {
    const next = { ...data, ...patch };
    setData(next);
    saveDraft(next);
  };

  const canOpenSignature =
    !!data.employeeName &&
    !!data.initials &&
    !!data.noticeAddress &&
    !!data.noticeCityStateZip &&
    !!data.noticeEmail &&
    !!data.noticePhone &&
    !savingDraft;

  const buildFields = () => ({
    employeeName: data.employeeName,
    initials: data.initials,
    noticeAddress: data.noticeAddress,
    noticeCityStateZip: data.noticeCityStateZip,
    noticeEmail: data.noticeEmail,
    noticePhone: data.noticePhone,
    laptopMake: data.laptopMake,
    laptopModel: data.laptopModel,
    laptopSerial: data.laptopSerial,
  });

  const livePdfUrl = useLivePdfPreview({
    builder: async () =>
      pdfUrl && config
        ? buildLaptopContractPdf(pdfUrl, config, {
            employeeName: data.employeeName,
            initials: data.initials,
            noticeAddress: data.noticeAddress,
            noticeCityStateZip: data.noticeCityStateZip,
            noticeEmail: data.noticeEmail,
            noticePhone: data.noticePhone,
            employeeSignedName: data.employeeName,
            schoolName: tokenData.school?.name || '',
            laptopMake: data.laptopMake,
            laptopModel: data.laptopModel,
            laptopSerial: data.laptopSerial,
            signatureDate: '',
          })
        : undefined,
    deps: [data, pdfUrl, config, tokenData.school?.name],
    enabled: !!pdfUrl && !!config,
  });

  const submitWithSignature = async (signaturePng: string) => {
    if (!config || !pdfUrl) return;
    const pdfBytes = await buildLaptopContractPdf(
      pdfUrl,
      config,
      {
        employeeName: data.employeeName,
        initials: data.initials,
        noticeAddress: data.noticeAddress,
        noticeCityStateZip: data.noticeCityStateZip,
        noticeEmail: data.noticeEmail,
        noticePhone: data.noticePhone,
        employeeSignedName: data.employeeName,
        schoolName: tokenData.school?.name || '',
        laptopMake: data.laptopMake,
        laptopModel: data.laptopModel,
        laptopSerial: data.laptopSerial,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
      signaturePng,
    );
    const pdfSigned = await blobToBase64(new Blob([pdfBytes as any], { type: 'application/pdf' }));

    await HireDocuments('').create({
      type: 'LAPTOP_CONTRACT',
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: {
        ...config,
        fields: buildFields(),
        signPng: signaturePng,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    });

    clearDraft();
    toast.success('Laptop Contract signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (!config || !pdfUrl || savingDraft) return;
    setSavingDraft(true);
    try {
      await HireDocuments('').create({
        type: 'LAPTOP_CONTRACT',
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: { ...config, fields: buildFields(), signPng: '' },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save progress.');
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Laptop User Contract &amp; Agreement</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Scroll through the contract below, fill the fields, then sign at the bottom.
      </p>

      {pdfUrl && <InlinePdfViewer url={livePdfUrl || pdfUrl} maxHeight="55vh" />}

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Identification</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Employee Name *</label>
          <input
            type="text"
            value={data.employeeName}
            onChange={(e) => update({ employeeName: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Initials *</label>
          <input
            type="text"
            value={data.initials}
            onChange={(e) => update({ initials: e.target.value.toUpperCase().slice(0, 6) })}
            placeholder="e.g., JS"
            style={layout.input}
          />
          <div style={{ fontSize: 12, color: colors.muted, marginTop: 4 }}>
            Will be applied to all 13 initial fields in the contract.
          </div>
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Contact Information</h4>
      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Address *</label>
        <input
          type="text"
          value={data.noticeAddress}
          onChange={(e) => update({ noticeAddress: e.target.value })}
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>City / State / ZIP *</label>
        <input
          type="text"
          value={data.noticeCityStateZip}
          onChange={(e) => update({ noticeCityStateZip: e.target.value })}
          placeholder="e.g., Orlando, FL, 32801"
          style={layout.input}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={layout.label}>Email *</label>
          <input
            type="email"
            value={data.noticeEmail}
            onChange={(e) => update({ noticeEmail: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Phone *</label>
          <input
            type="tel"
            value={data.noticePhone}
            onChange={(e) => update({ noticePhone: formatPhone(e.target.value) })}
            placeholder="(555) 123-4567"
            style={layout.input}
          />
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Issued Laptop (optional)</h4>
      <p style={{ color: colors.muted, fontSize: 12, marginTop: 0 }}>
        These fields can be filled in by your administrator at the time of issuance.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={layout.label}>Make</label>
          <input
            type="text"
            value={data.laptopMake}
            onChange={(e) => update({ laptopMake: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Model</label>
          <input
            type="text"
            value={data.laptopModel}
            onChange={(e) => update({ laptopModel: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Serial Number</label>
          <input
            type="text"
            value={data.laptopSerial}
            onChange={(e) => update({ laptopSerial: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title="Sign Laptop Contract"
        submitLabel="Accept & Sign"
        previewPdfUrl={livePdfUrl}
      />
    </StepShell>
  );
};

export default LaptopContractStep;
